/**
 * ImpactScoresTab.tsx — VER-59 "Impact Scores" tab (read-only, mock data).
 *
 * MOUNTING NOTE:
 *   This component is designed to be a TAB PANEL on the VER-27 participant
 *   detail page (not yet built — Phase 1 / Sean). When VER-27 ships, mount
 *   this as:
 *
 *     <ImpactScoresTab assessmentId={assessmentId} />
 *
 *   inside a MUI <TabPanel> keyed to the "Impact Scores" tab index.
 *   The mock host page at ImpactScoresMockPage.tsx demonstrates this wiring.
 *
 * DATA FLOW (future):
 *   assessmentId → fetch from VER-55 endpoint → WemwbsScores[]
 *   assessmentId → fetch from VER-76 endpoint → MoodPoint[]
 *   For now, both are sourced from impact.mock.ts.
 *
 * RBAC:
 *   TODO [RBAC]: Wrap with permit("AssessImpactView") once the permit code is
 *   defined. Run verbal-rbac-review before merge. Current render is unguarded
 *   (appropriate for mock-only phase).
 *
 * ANONYMITY:
 *   - No participant names, emails, or internal IDs are rendered here.
 *   - participantRef labels ("P1"…) are opaque display keys only.
 *   - Mood data is group-average only (see MoodLineChart).
 *   - Run verbal-anonymity-check before wiring to real data.
 *   - TODO [VER-55/VER-59]: Enforce k-anonymity guard (suppress if cohort < 5)
 *     server-side before returning scores to this surface.
 */

import React, { FC } from "react";
import { Alert, Box, Divider, Grid, Tooltip, Typography } from "@mui/material";
import { TrendingUpOutlined } from "@mui/icons-material";
import { VCard } from "../../../core/components/VCard";
import { HeadlineFigureWidget } from "../../dashboard/Widgets/Headlines/HeadlineFigureWidget";
import { VPillPositive, VPillNegative, VPill } from "../../../core/components/VPill";
import { WemwbsBarChart } from "./WemwbsBarChart";
import { MoodLineChart } from "./MoodLineChart";
import { MOCK_WEMWBS_SCORES, MOCK_WEMWBS_SUMMARY, MOCK_MOOD_SERIES } from "./impact.mock";

// ---------------------------------------------------------------------------
// Internal: summary stat card — composes the shared HeadlineFigureWidget (outlined
// card) instead of re-implementing it; the tooltip + polarity colour live in the
// children. minHeight="auto" keeps the stat row compact, left-aligned to match.
// ---------------------------------------------------------------------------

type SummaryStatProps = {
    label: string;
    value: string | number;
    tooltip?: string;
    positive?: boolean;
};

const SummaryStat: FC<SummaryStatProps> = ({ label, value, tooltip, positive }) => (
    <HeadlineFigureWidget minHeight="auto" centeredHeader={false}>
        <Tooltip title={tooltip ?? ""} placement="top">
            <Box>
                <Typography
                    variant="h3"
                    sx={{
                        color: positive !== undefined ? (positive ? "success.main" : "error.main") : "primary.main",
                    }}
                >
                    {value}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {label}
                </Typography>
            </Box>
        </Tooltip>
    </HeadlineFigureWidget>
);

// ---------------------------------------------------------------------------
// Internal: delta pill — positive/neutral/negative
// ---------------------------------------------------------------------------

const DeltaPill: FC<{ delta: number }> = ({ delta }) => {
    if (delta > 0) {
        return <VPillPositive size="small" label={`+${delta}`} />;
    }
    if (delta < 0) {
        return <VPillNegative size="small" label={String(delta)} />;
    }
    return <VPill size="small" label="0" />;
};

// ---------------------------------------------------------------------------
// Main exported tab component
// ---------------------------------------------------------------------------

export type ImpactScoresTabProps = {
    /**
     * assessmentId — passed from the host page (VER-27) to scope API calls.
     * Unused in mock phase; reserved for the data-wiring wave.
     */
    assessmentId?: string | number;
};

export const ImpactScoresTab: FC<ImpactScoresTabProps> = () => {
    // TODO [DATA]: Replace these constants with data fetched using assessmentId
    // from VER-55 (WEMWBS scores) and VER-76 (mood series).
    const scores = MOCK_WEMWBS_SCORES;
    const summary = MOCK_WEMWBS_SUMMARY;
    const moodSeries = MOCK_MOOD_SERIES;

    return (
        <Box>
            {/* Mock data banner — remove when wired to real API */}
            <Alert severity="warning" sx={{ mb: 3 }}>
                <strong>Mock data only.</strong> This tab will display live WEMWBS scores once VER-55 (SurveyMonkey
                integration) and VER-76 (session mood) are live.
            </Alert>

            {/* ----------------------------------------------------------------
                Section 1: WEMWBS summary stats
            ---------------------------------------------------------------- */}
            <Box mb={3}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <TrendingUpOutlined color="primary" />
                    <Typography variant="h5">WEMWBS Impact Summary</Typography>
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <SummaryStat
                            label="Mean pre-score"
                            value={summary.meanPreScore}
                            tooltip="Average WEMWBS total score before the programme (range 14–70)"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <SummaryStat
                            label="Mean post-score"
                            value={summary.meanPostScore}
                            tooltip="Average WEMWBS total score after the programme (range 14–70)"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <SummaryStat
                            label="Mean improvement"
                            value={`+${summary.meanDelta}`}
                            positive={summary.meanDelta > 0}
                            tooltip="Average score change (post − pre). Positive = wellbeing improvement."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <SummaryStat
                            label="Participants improved"
                            value={`${summary.improvedCount} / ${summary.totalCount}`}
                            positive={summary.improvedCount > summary.totalCount / 2}
                            tooltip="Number of participants whose post-score exceeded their pre-score"
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* ----------------------------------------------------------------
                Section 2: WEMWBS pre/post grouped bar chart
            ---------------------------------------------------------------- */}
            <Box mb={3}>
                <VCard>
                    <WemwbsBarChart scores={scores} />
                </VCard>
            </Box>

            {/* ----------------------------------------------------------------
                Section 3: Individual delta table
                Read-only; participantRefs are anonymised labels only.
            ---------------------------------------------------------------- */}
            <Box mb={3}>
                <VCard
                    title="Improvement by participant"
                    subtitle="Anonymised participant references (P1, P2…) — no personal data."
                >
                    <Box
                        component="table"
                        sx={{
                            width: "100%",
                            borderCollapse: "collapse",
                            "& th, & td": {
                                textAlign: "left",
                                py: 1,
                                px: 1.5,
                                borderBottom: (t) => `1px solid ${t.palette.divider}`,
                                fontSize: "0.875rem",
                            },
                            "& th": { fontWeight: 700 },
                        }}
                    >
                        <thead>
                            <tr>
                                <th>Ref</th>
                                <th>Pre</th>
                                <th>Post</th>
                                <th>Delta</th>
                            </tr>
                        </thead>
                        <tbody>
                            {scores.map((s) => (
                                <tr key={s.participantRef}>
                                    <td>{s.participantRef}</td>
                                    <td>{s.pre}</td>
                                    <td>{s.post}</td>
                                    <td>
                                        <DeltaPill delta={s.delta} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Box>
                </VCard>
            </Box>

            {/* ----------------------------------------------------------------
                Section 4: S2–S5 mood trend — VISUALLY SEPARATE per JK spec.
                Separated by a labelled divider so the surface hierarchy is clear.
            ---------------------------------------------------------------- */}
            <Divider sx={{ my: 3 }}>
                <Typography variant="overline" color="textSecondary">
                    Session mood (separate source — VER-76)
                </Typography>
            </Divider>

            <Box mb={2}>
                <VCard>
                    <MoodLineChart series={moodSeries} />
                </VCard>
            </Box>
        </Box>
    );
};
